import { yupResolver } from '@hookform/resolvers/yup'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { PageTitle } from '@/components'
import { MultiSelect, MultiSelectOption } from '@/components/multi-select'
import { Button, Input, Modal, Textarea } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import { FormUploadPreview } from '@/core/components/FormUploadPreview'
import {
  createVendorProduct,
  deleteVendorProduct,
  getVendorTags,
  IVendorProduct,
  IVendorTag,
  updateVendorProduct,
} from '@/core/services/vendors'
import { uploadImage } from '@/core/services/upload-image'
import { DeleteConfirmationModal } from '@/home/components/DeleteConfirmationModal'
import { MessageBox } from '@/home/components/messages/MessageBox'

interface CreateProductModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vendorId: string
  onSuccess: () => void
  onError: (message: string) => void
  product?: IVendorProduct | null
}

interface IProductFormValues {
  name: string
  description: string
  price: number
  photo: string
  tags: MultiSelectOption[]
}

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  description: yup.string().required('Description is required'),
  price: yup
    .number()
    .typeError('Price must be a number')
    .min(0, 'Price must be positive')
    .required('Price is required'),
  photo: yup.string(),
  tags: yup.array().of(
    yup.object().shape({
      label: yup.string().required(),
      value: yup.string().required(),
    }),
  ),
})

export const CreateProductModal = ({
  open,
  onOpenChange,
  vendorId,
  onSuccess,
  onError,
  product,
}: CreateProductModalProps) => {
  const isEditMode = !!product
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [tagOptions, setTagOptions] = useState<MultiSelectOption[]>([])

  const [image, setImage] = useState('')
  const [imageName, setImageName] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [isErrorRatio, setIsErrorRatio] = useState(false)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields },
    reset,
    setValue,
    watch,
    control,
  } = useForm<IProductFormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as any,
    defaultValues: { name: '', description: '', price: 0, photo: '', tags: [] },
  })

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await getVendorTags(vendorId)
      setTagOptions(
        data.map((t: IVendorTag) => ({ label: t.name, value: t.id })),
      )

      if (product) {
        setValue(
          'tags',
          product.tags.map((t) => ({
            label: t,
            value: data.find((tg: IVendorTag) => tg.name === t)?.id || '',
          })),
          { shouldValidate: true },
        )
      }
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    }
  }, [vendorId, product])

  useEffect(() => {
    if (!open) return
    fetchTags()
    if (product) {
      setValue('name', product.name, { shouldValidate: true })
      setValue('description', product.description || '', {
        shouldValidate: true,
      })
      setValue('price', product.price, { shouldValidate: true })
      setValue('photo', product.imageLink, { shouldValidate: true })
      setImage(product.imageLink)
    }
    return () => {
      reset({ name: '', description: '', price: 0, photo: '', tags: [] })
      setImage('')
      setImageName('')
    }
  }, [open, product])

  const handleClose = () => {
    onOpenChange(false)
  }

  const submit = async (values: IProductFormValues) => {
    try {
      const payload = {
        name: values.name,
        description: values.description,
        imageLink: values.photo,
        price: values.price,
        tags: (values.tags || []).map((t) => t.value),
      }
      if (isEditMode) {
        const { status } = await updateVendorProduct(
          vendorId,
          product.id,
          payload,
        )
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
          onSuccess()
        }
      } else {
        const { status } = await createVendorProduct(vendorId, {
          ...payload,
        })
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
          onSuccess()
        }
      }
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    }
    handleClose()
  }

  const handleDelete = async () => {
    if (!product) return
    try {
      await deleteVendorProduct(vendorId, product.id)
      onSuccess()
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    }
    handleClose()
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(o) => {
          if (!o) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="Vendor Entity"
              title={
                <span className="flex items-center gap-2">
                  {isEditMode ? 'Edit' : 'Add New'}{' '}
                  <span className="text-accent-1">Product</span>
                </span>
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {isEditMode ? (
              <Button
                onClick={() => setDeleteModalOpen(true)}
                variant="destructive"
                className="mr-auto"
              >
                Delete Product
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={isEditMode && Object.keys(dirtyFields).length === 0}
                form="create-product-form"
                type="submit"
                variant="primary"
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="create-product-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Product Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Product Name"
            />
          </FormGroup>

          <FormGroup label="Select Tags">
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  options={tagOptions}
                  value={field.value || []}
                  onChange={(next) => {
                    field.onChange(next)
                  }}
                  placeholder="Select Tags"
                  searchPlaceholder="Search tags..."
                />
              )}
            />
          </FormGroup>

          <FormGroup
            label="Price (default currency US Dollar)"
            error={errors?.price?.message}
          >
            <Input
              isInvalid={!!errors?.price?.message}
              {...register('price')}
              type="number"
              placeholder="Enter Price"
            />
          </FormGroup>

          <FormGroup label="Description" error={errors?.description?.message}>
            <Textarea
              isInvalid={!!errors?.description?.message}
              {...register('description')}
              placeholder="Enter Description"
              rows={3}
            />
          </FormGroup>

          <div className="my-4 h-px w-full bg-monochrome-120" />

          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-monochrome-1">Photo</h3>
            <FormUploadPreview
              control={
                <Input
                  multiple={false}
                  accept="image/*"
                  isInvalid={!!errors?.photo?.message}
                  name="photo"
                  onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                    const file = e?.target?.files?.[0]
                    if (file) {
                      if (file.size > 1_000_000) {
                        setIsError(true)
                        setShowMessageBox(true)
                        setMessage('The photo is too large (1 MB)')
                        return
                      }
                      const data = await uploadImage(
                        file,
                        setIsUploading,
                        setIsError,
                        setShowMessageBox,
                        setMessage,
                        setIsErrorRatio,
                        'VENDOR_PRODUCT',
                      )
                      if (data.photoUrl) {
                        setImage(data.photoUrl)
                        setImageName(file.name)
                        setValue('photo', data.photoUrl, {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }
                    }
                  }}
                  type="file"
                  className="bg-monochrome-5-50"
                />
              }
              imageName={imageName}
              isUploading={isUploading}
              url={image}
              onRemove={() => {
                setImage('')
                setImageName('')
                setValue('photo', '', { shouldDirty: true })
              }}
              imageStyle={{ objectFit: 'contain' }}
              label="Image (min 500x500 under 1mb)"
              previewLabel="Image (min 500x500 under 1mb)"
            />
          </div>
        </form>
      </Modal>

      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteModalOpen}
        setDeleteConfirmationModal={setDeleteModalOpen}
        deleteFunc={handleDelete}
        deleteTitle="Product"
        deletedItem={product?.name}
      />

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
